import { Firestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { Firebase$ } from './firebase$';
import { inject } from '@angular/core';
import {
    DocumentData,
    DocumentReference,
    QueryFieldFilterConstraint,
    serverTimestamp,
    Timestamp,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    setDoc,
    updateDoc,
    where,
} from 'firebase/firestore';

export type COLNAME =
    | 'projects'
    | 'teams'
    | 'teamCode'
    | 'users'
    | 'userTags'
    | 'storage'
    | 'attachment'
    | 'projectUserDetails'
    | 'channel'
    | 'public'
    | 'externalAffiliation'
    | 'userGroups'
    | 'books'
    | 'bookTeammateLs'
    | 'bookTeamMember'
    | 'bookId'
    | 'bookContents'
    | 'bookUserDetails'
    | 'bookStorage'
    | 'accessLists/users/whitelist';
type FIELD =
    | 'userId'
    | 'ownerId'
    | 'projectId'
    | 'email'
    | 'channelId'
    | 'bookId'
    | 'pageId'
    | 'instanceId'
    | 'timestamp'
    | 'kind'
    | 'expires'
    | 'tag';
type OPERATOR = '==' | '!=' | 'in' | '>';
export type QF = {
    field: FIELD;
    value: string | string[] | Timestamp | '__SORT__';
    operator?: OPERATOR;
    sort?: 'asc' | 'desc';
};
export class FirebaseFirestore {
    private _firestore: Firestore;

    constructor(private _firebase: Firebase$) {
        this._firestore = inject(Firestore);
    }

    async getObject(collection: COLNAME, objId: string) {
        try {
            const docRef = doc(this._firestore, collection, objId);
            const resp = await getDoc(docRef);
            return resp.data();
        } catch (e) {
            console.error('COLNAME:', collection, 'OBJECT ID', objId);
            console.error(e);
            return null;
        }
    }

    async getObjectLsFromIdLs(collection: COLNAME, objIdLs: string[]) {
        try {
            const resp = await this._firebase.callFunction('getObjectLsFromIdLs', {
                collection: collection,
                idLs: objIdLs,
            });
            if (resp === 'ERROR') return [];
            return resp.data;
        } catch (e) {
            console.error('COLNAME:', collection, 'OBJECT ID', objIdLs);
            console.error(e);
            return [];
        }
    }

    async getObjectLs(collection: COLNAME, queryLs: QF | QF[]) {
        try {
            const resultLs: any[] = [];
            const qs = await this.getDocLs(collection, queryLs);
            qs.forEach((doc) => resultLs.push(doc.data()));
            return resultLs;
        } catch (e) {
            console.error('GET-OBJECT-LS', collection, queryLs);
            return [];
        }
    }

    async subscribeObject(collection: COLNAME, objId: string, fun: (data: DocumentData) => void) {
        const docRef = doc(this._firestore, collection, objId);
        return onSnapshot(docRef, fun);
    }

    get timestamp() {
        return Timestamp.now();
    }

    private _createQuery(collection: COLNAME, queryLs: QF | QF[], maxCount = 100) {
        if (!Array.isArray(queryLs)) queryLs = [queryLs];

        const orderLs: any[] = [];

        const whereLs = queryLs
            .map((q) => {
                if (!q.operator) q.operator = '==';
                if (q.operator === 'in' && (!Array.isArray(q.value) || q.value.length === 0))
                    return undefined;
                if (q.sort) orderLs.push({ field: q.field, direction: q.sort });
                if (q.value === '__SORT__') return undefined;
                return where(q.field, q.operator, q.value);
            })
            .filter((x) => x) as QueryFieldFilterConstraint[];
        if (!whereLs) return null;

        const q = query(
            this._collection(collection),
            ...whereLs,
            ...orderLs.map((x) => orderBy(x.field, x.direction)),
            limit(maxCount),
        );
        return q;
    }

    async getDocLs(collection: COLNAME, queryLs: QF | QF[]) {
        try {
            const q = this._createQuery(collection, queryLs);
            if (!q) return [];
            return await getDocs(q);
        } catch (e) {
            console.error('getDocLs', collection, queryLs);
            console.error(e);
            return [];
        }
    }

    async getRefLs(collection: COLNAME, queryLs: QF | QF[]) {
        try {
            const qs = await this.getDocLs(collection, queryLs);
            const resultLs: any[] = [];
            qs.forEach((doc) => resultLs.push(doc.ref));
            return resultLs as DocumentReference[];
        } catch (e) {
            console.error('getRefLs', collection, queryLs);
            console.error(e);
            return [];
        }
    }

    async listenQuery(
        collection: COLNAME,
        queryLs: QF | QF[],
        fun: (x: any) => void,
        maxCount = 100,
    ) {
        try {
            const q = this._createQuery(collection, queryLs, maxCount);
            // if (!q) return null
            return onSnapshot(q!, fun);
        } catch (e) {
            console.error('listenQuery', collection, queryLs);
            console.error(e);
            return undefined;
        }
    }

    private _collection(tp: COLNAME) {
        return collection(this._firestore, tp);
    }

    private _escapeDocId(txt: string) {
        return txt.replace(/\//g, '|');
    }

    addTimestampField(data: any, fieldName = 'timestamp') {
        data[fieldName] = serverTimestamp();
        return data;
    }

    async updateDocument(colName: COLNAME, docId: string, data: DocumentData) {
        try {
            const docRef = doc(this._firestore, colName, docId);
            await updateDoc(docRef, data);
            return true;
        } catch (e) {
            console.error('UPDATE DOCUMENT', colName, docId, data, e);
            return false;
        }
    }

    async addDocument(colName: COLNAME, docId: string, data: any) {
        try {
            // console.log("FIRESTORE addDocument", colName, docId, data)
            // console.log(data)
            await setDoc(doc(this._firestore, colName, this._escapeDocId(docId)), data, {
                merge: false,
            });
            return true;
        } catch (e) {
            console.error('Error adding document: ', colName, docId, data);
            console.error(e);
            return false;
        }
    }

    async deleteDocumentLs(ls: DocumentReference[]) {
        try {
            for (const ref of ls) {
                await deleteDoc(ref);
            }
            return true;
        } catch (e) {
            console.error('Error deleting documents: ', ls);
            console.error(e);
            return false;
        }
    }

    async deleteDocument(colName: COLNAME, docId: string) {
        try {
            return await deleteDoc(doc(this._firestore, colName, this._escapeDocId(docId)));
        } catch (e) {
            console.error('Error deleting document: ', colName, docId);
        }
    }

    async removeStoragePath(remotePath: string) {
        try {
            return await deleteDoc(doc(this._firestore, 'storage', remotePath));
        } catch (e) {
            console.error('Error deleting storagePath: ', remotePath);
        }
    }
}
